import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Bitcoin, LineChart, DollarSign, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CheckoutModal from "@/components/CheckoutModal";
import { CompareTable } from "@/components/CompareTable";

const PLANS = [
  {
    id: "bundle",
    name: "ALL ACCESS",
    icon: Sparkles,
    monthly: { price: 140, original: 180 },
    yearly: { price: 1400, original: 1800 },
    tagline: "Every market. Every idea. Full access to crypto, stocks & forex — plus SynDex.",
    features: [
      "All 3 private trading ideas rooms",
      "Memecoin plays & on-chain alpha",
      "Earnings, options & FX setups",
      "Access to owner & all mentors",
      "SynDex AI trading bot access",
      "Priority Q&A & live rooms",
    ],
    accent: "from-white/10 to-transparent",
    featured: true,
  },
  {
    id: "crypto",
    name: "CRYPTO - MEMECOINS",
    icon: Bitcoin,
    monthly: { price: 70, original: 100 },
    yearly: { price: 700, original: 1000 },
    tagline: "Live trading ideas across BTC, ETH, alts, memecoin plays & on-chain calls.",
    features: [
      "Private crypto trading ideas room",
      "Memecoin plays & early alpha",
      "On-chain & narrative breakdowns",
      "Weekly market outlook",
      "Access to owner & mentors",
      "SynDex AI trading bot access",
    ],
    accent: "from-amber-500/10 to-transparent",
  },
  {
    id: "forex",
    name: "FOREX",
    icon: DollarSign,
    monthly: { price: 70, original: 100 },
    yearly: { price: 700, original: 1000 },
    tagline: "Institutional-style FX setups across majors, minors & exotics.",
    features: [
      "Private forex trading ideas room",
      "Session-based analysis",
      "Risk & psychology coaching",
      "Access to owner & mentors",
    ],
    accent: "from-sky-500/10 to-transparent",
  },
  {
    id: "stocks",
    name: "STOCKS",
    icon: LineChart,
    monthly: { price: 70, original: 100 },
    yearly: { price: 700, original: 1000 },
    tagline: "Equities, options and earnings trades from active operators.",
    features: [
      "Private stocks trading ideas room",
      "Earnings & catalyst plays",
      "Options flow breakdowns",
      "Access to owner & mentors",
    ],
    accent: "from-emerald-500/10 to-transparent",
  },
];

export default function Pricing() {
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
  const [checkout, setCheckout] = useState(null); // { plan, billing, planLabel, price, priceOriginal }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="pricing-page">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-10 px-4 md:px-6">
        <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            MEMBERSHIP // PRICING
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl tracking-tight">
            Pick your market
          </h1>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto text-base md:text-lg">
            Go all-in with All Access, or pick a single market. Instant group access on payment.
          </p>

          {/* Billing toggle — glass liquid style */}
          <div className="mt-10 inline-block relative">
            {/* Floating SAVE badge above yearly side */}
            <div
              aria-hidden
              className={`absolute -top-3 right-6 md:right-8 z-20 font-mono-serif text-[9px] tracking-[0.2em] px-2.5 py-1 rounded-full border transition-all duration-500 ${
                billing === "yearly"
                  ? "bg-emerald-400 text-black border-emerald-300 shadow-[0_0_20px_rgba(52,211,153,0.5)]"
                  : "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
              }`}
            >
              SAVE 17%
            </div>

            <div className="relative grid grid-cols-2 p-1.5 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_30px_-10px_rgba(0,0,0,0.6)] w-[280px] md:w-[320px]">
              {/* Liquid glass indicator */}
              <span
                aria-hidden
                className="pointer-events-none absolute top-1.5 bottom-1.5 rounded-full transition-all duration-[550ms] ease-[cubic-bezier(0.4,1.4,0.6,1)]"
                style={{
                  left: billing === "monthly" ? "6px" : "50%",
                  width: "calc(50% - 6px)",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,0.98) 100%)",
                  boxShadow:
                    "0 8px 24px -6px rgba(255,255,255,0.35), 0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.05)",
                }}
              />
              {/* Specular highlight */}
              <span
                aria-hidden
                className="pointer-events-none absolute top-[3px] h-1 rounded-full transition-all duration-[550ms] ease-[cubic-bezier(0.4,1.4,0.6,1)] opacity-70"
                style={{
                  left: billing === "monthly" ? "18px" : "calc(50% + 12px)",
                  width: "calc(50% - 30px)",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)",
                  filter: "blur(1px)",
                }}
              />

              <button
                data-testid="billing-monthly-btn"
                onClick={() => setBilling("monthly")}
                className={`relative z-10 font-mono-serif text-[10px] md:text-[11px] tracking-[0.3em] py-3 rounded-full transition-colors duration-500 text-center ${
                  billing === "monthly" ? "text-black" : "text-white/70 hover:text-white"
                }`}
              >
                MONTHLY
              </button>
              <button
                data-testid="billing-yearly-btn"
                onClick={() => setBilling("yearly")}
                className={`relative z-10 font-mono-serif text-[10px] md:text-[11px] tracking-[0.3em] py-3 rounded-full transition-colors duration-500 text-center ${
                  billing === "yearly" ? "text-black" : "text-white/70 hover:text-white"
                }`}
              >
                YEARLY
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PLANS.map((plan) => {
            const period = billing === "monthly" ? plan.monthly : plan.yearly;
            const suffix = billing === "monthly" ? "/month" : "/year";
            return (
              <div
                key={plan.id}
                data-testid={`plan-${plan.id}`}
                className={`relative frame p-7 md:p-8 flex flex-col ${
                  plan.featured ? "lg:-translate-y-3 border-white/25" : ""
                }`}
              >
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${plan.accent} pointer-events-none`} />
                {plan.comingSoon && (
                  <>
                    <div
                      aria-hidden
                      className="absolute inset-0 rounded-3xl backdrop-blur-md bg-black/40 z-20 pointer-events-none"
                    />
                    <div
                      data-testid="coming-soon-overlay"
                      className="absolute inset-0 rounded-3xl z-30 flex items-center justify-center pointer-events-none"
                    >
                      <div className="text-center px-6">
                        <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/50">STATUS</p>
                        <h3 className="mt-3 font-display text-4xl md:text-5xl bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">
                          Coming<br/>Soon
                        </h3>
                        <p className="mt-4 font-mono-serif text-[10px] tracking-[0.3em] text-white/60">
                          LAUNCHING SHORTLY
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-black font-mono-serif text-[10px] tracking-[0.3em] px-3 py-1 rounded-full whitespace-nowrap">
                    POPULAR
                  </div>
                )}
                <div className="relative z-10 flex items-center gap-3">
                  <plan.icon size={22} className="text-white" />
                  <h3 className="font-mono-serif text-xs tracking-[0.35em]">{plan.name}</h3>
                </div>

                <div className="relative z-10 mt-6">
                  <div className="flex items-baseline gap-3">
                    <span
                      data-testid={`price-original-${plan.id}`}
                      className="font-display text-2xl md:text-3xl text-white/40 line-through decoration-red-400/70 decoration-2"
                    >
                      £{period.original}
                    </span>
                    <span className="font-mono-serif text-[10px] tracking-[0.2em] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                      SAVE £{period.original - period.price}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span
                      data-testid={`price-current-${plan.id}`}
                      className="font-display text-5xl md:text-6xl"
                    >
                      £{period.price}
                    </span>
                    <span className="text-white/50 text-sm">{suffix}</span>
                  </div>
                </div>

                <p className="relative z-10 mt-4 text-white/60 text-sm leading-relaxed min-h-[3.5rem]">
                  {plan.tagline}
                </p>
                <ul className="relative z-10 mt-6 space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                      <Check size={16} className="mt-0.5 text-white shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (plan.comingSoon) return;
                    setCheckout({
                      plan: plan.id,
                      billing,
                      planLabel: plan.name,
                      price: period.price,
                      priceOriginal: period.original,
                    });
                  }}
                  data-testid={`join-${plan.id}-btn`}
                  aria-disabled={plan.comingSoon || undefined}
                  className={`relative z-10 mt-8 inline-flex items-center justify-center gap-2 font-mono-serif text-[11px] tracking-[0.3em] px-6 py-3.5 rounded-full transition ${
                    plan.comingSoon
                      ? "border border-white/15 text-white/50 cursor-not-allowed"
                      : "btn-shimmer text-black hover:opacity-95"
                  }`}
                >
                  {plan.comingSoon
                    ? "NOTIFY ME"
                    : plan.id === "bundle" ? "GET ALL ACCESS" : `JOIN ${plan.name}`}
                  {!plan.comingSoon && <ArrowRight size={14} />}
                </Link>
                <p className="relative z-10 mt-3 text-center text-[10px] text-white/40 font-mono-serif tracking-[0.25em]">
                  INSTANT ACCESS ON PAYMENT
                </p>
              </div>
            );
          })}
        </div>

        <CompareTable plans={PLANS} billing={billing} />

        <div className="max-w-3xl mx-auto mt-16 text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">QUESTIONS?</p>
          <h3 className="mt-3 font-display text-3xl md:text-4xl">Talk to the team</h3>
          <p className="mt-3 text-white/55">
            Not sure which room to join? Reach out and we'll help you pick the right seat.
          </p>
          <a
            href="https://t.me/issyflash"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bundle-contact-btn"
            className="mt-6 inline-flex items-center gap-2 border border-white/20 text-white font-mono-serif text-[11px] tracking-[0.3em] px-6 py-3.5 rounded-full hover:bg-white/5 transition"
          >
            TALK TO THE SYNDICATE
          </a>
        </div>
      </section>

      <CheckoutModal
        open={!!checkout}
        onClose={() => setCheckout(null)}
        plan={checkout?.plan}
        billing={checkout?.billing}
        planLabel={checkout?.planLabel}
        price={checkout?.price}
        priceOriginal={checkout?.priceOriginal}
      />

      <Footer />
    </div>
  );
}
